/**
 * <SubscriptionStateCard> — l'abonnement en cours, en une carte (organism, L0.1).
 *
 * Reprend `renderStateCard` du portail legacy (`ParentSubscriptionPortalScreen.js:904-1089`), dans
 * le même ordre : l'offre et son volume, les chips d'objectifs, le statut, la fin de couverture,
 * puis les lignes datées (prochain paiement, mise en pause, reprise, fin d'engagement).
 *
 * Aucune logique de dates ici : chaque ligne arrive **déjà formulée** par le view-model, avec son
 * glyphe. Les gardes du legacy (statut masqué quand la couverture est passée, ligne d'engagement
 * masquée en fenêtre été…) se traduisent donc par une prop absente, jamais par une condition
 * recopiée dans le DS.
 *
 * C'est **la** carte en relief de la page Abonnement : elle porte ce que le parent vient y lire.
 * Tout le reste du portail est à plat (`CLAUDE.md`, « Une seule surface en relief par page »).
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Card } from "../../molecules/Card"
import { HorizontalList } from "../../atoms/HorizontalList"
import { Icon } from "../../atoms/icons/Icon"
import { ObjectiveHoursChip } from "../../molecules/ObjectiveHoursChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SubscriptionLine } from "../../molecules/SubscriptionLine"
import { Text } from "../../atoms/typography/Text"
import { type UISubscriptionLine, type UISubscriptionObjective } from "../../../ports/primary/ui/types"

export type SubscriptionStateCardProps = {
    /** Nom de l'offre en cours — « Cours partagés », « Cours particuliers », « Mode Autonome »… */
    offerLabel?: string | null
    /** Volume hebdomadaire — « 2h/sem ». Absent pour le Mode Autonome. */
    hoursLabel?: string | null
    /** Ce qu'on affiche quand il n'y a pas d'offre. */
    emptyLabel?: string
    /** Statut d'abonnement — « Abonnement actif », « Abonnement mis en pause »… */
    statusLabel?: string | null
    /** Fin de couverture du produit — « se termine le 31 août 2026 ». */
    endLine?: string | null
    /** Un objectif pédagogique en cours = une chip. */
    objectives?: UISubscriptionObjective[]
    /** Lignes datées, dans l'ordre voulu par le view-model. */
    lines?: UISubscriptionLine[]
    testID?: string
}

export function SubscriptionStateCard(props: SubscriptionStateCardProps) {
    const {
        offerLabel,
        hoursLabel,
        emptyLabel = "Aucun abonnement actif",
        statusLabel,
        endLine,
        objectives = [],
        lines = [],
        testID,
    } = props

    // Le volume se colle à l'offre — « Cours partagés · 2h/sem ».
    const offerLine = [offerLabel, hoursLabel].filter(Boolean).join(" · ")

    return (
        <Card p="4" testID={testID}>
            <Stack gap="3">
                {offerLine ? (
                    <Row gap="2">
                        <Text size="text-base" weight="bold" color="primary" style={styles.shrink}>
                            {offerLine}
                        </Text>
                        <Icon name="fire" size={16} color="warning" />
                    </Row>
                ) : (
                    <Text weight="bold" color="text-muted">
                        {emptyLabel}
                    </Text>
                )}

                {objectives.length > 0 && (
                    <HorizontalList gap="2">
                        {objectives.map((objective) => (
                            <ObjectiveHoursChip
                                key={objective.id}
                                subjects={objective.subjects}
                                hours={objective.hours}
                            />
                        ))}
                    </HorizontalList>
                )}

                {(statusLabel != null || endLine != null) && (
                    <Stack gap="1">
                        {statusLabel != null && <Text weight="medium">{statusLabel}</Text>}
                        {endLine != null && (
                            <Text size="text-sm" weight="semibold" color="text-muted">
                                {endLine}
                            </Text>
                        )}
                    </Stack>
                )}

                {lines.length > 0 && (
                    <Stack gap="2">
                        {lines.map((line, index) => (
                            <SubscriptionLine key={`${line.icon}-${index}`} line={line} />
                        ))}
                    </Stack>
                )}
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    // Le libellé se replie plutôt que de pousser son glyphe hors de la carte.
    shrink: {
        flexShrink: 1,
    },
}))
